import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { claimsAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, Clock, CheckCircle, AlertCircle, TrendingUp } from 'lucide-react';
import { CLAIM_STATUS, formatDate, formatCurrency, getStatusColor } from '@/utils/constants';
import { Link } from 'react-router-dom';

const ClientDashboard = () => {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    settled: 0,
  });

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    try {
      const response = await claimsAPI.getAll({ limit: 100 });
      const userClaims = response.data;
      setClaims(userClaims);
      
      setStats({
        total: userClaims.length,
        pending: userClaims.filter(c => 
          [CLAIM_STATUS.SUBMITTED, CLAIM_STATUS.UNDER_REVIEW, CLAIM_STATUS.INVESTIGATION].includes(c.status)
        ).length,
        approved: userClaims.filter(c => c.status === CLAIM_STATUS.APPROVED).length,
        settled: userClaims.filter(c => c.status === CLAIM_STATUS.SETTLED).length,
      });
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Welcome back, {user?.full_name}
        </h1>
        <p className="text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
          Here's an overview of your insurance claims
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-slate-200 hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Total Claims
            </CardTitle>
            <FileText className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {stats.total}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Pending Review
            </CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {stats.pending}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Approved
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {stats.approved}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Settled
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {stats.settled}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Link to="/submit-claim">
              <Button data-testid="submit-new-claim-button" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                <FileText className="h-4 w-4 mr-2" />
                Submit New Claim
              </Button>
            </Link>
            <Link to="/claims">
              <Button variant="outline" data-testid="view-all-claims-button" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                View All Claims
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Recent Claims */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Claims</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>Loading claims...</div>
          ) : claims.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-4" style={{ fontFamily: 'Public Sans, sans-serif' }}>No claims yet</p>
              <Link to="/submit-claim">
                <Button data-testid="submit-first-claim-button" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                  Submit Your First Claim
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {claims.slice(0, 5).map((claim) => (
                <Link key={claim.id} to={`/claims/${claim.id}`}>
                  <div className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 hover:shadow-sm transition-all">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span 
                            className="font-mono text-sm font-medium text-slate-900"
                            style={{ fontFamily: 'JetBrains Mono, monospace' }}
                          >
                            {claim.claim_id}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(claim.status)}`}>
                            {claim.status}
                          </span>
                        </div>
                        <div className="text-sm text-slate-600 mb-1" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                          <strong>{claim.claim_type}</strong> - {claim.insured_name}
                        </div>
                        <div className="text-sm text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                          Filed: {formatDate(claim.created_at)} | Est. Loss: {formatCurrency(claim.estimated_loss)}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientDashboard;
